import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  ArrowRight,
  TrendingUp,
  Users,
  ExternalLink,
  Building2,
  Target,
  Award
} from 'lucide-react';

const CaseStudyCard = ({ caseStudy, index }) => {
  // Industry-specific icons
  const industryIcons = {
    'Telecommunications': Building2,
    'Banking & Finance': Building2,
    'Retail & E-commerce': Building2,
    'Aviation': Building2,
    'Transportation': Building2,
    'Postal Services': Building2
  };

  const IconComponent = industryIcons[caseStudy.industry] || Building2;

  return (
    <section className={`py-24 ${index % 2 === 0 ? 'bg-background' : 'bg-muted/30'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Content Side */}
          <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
            {/* Industry Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <IconComponent className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">{caseStudy.industry}</span>
            </div>

            {/* Title */}
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
              {caseStudy.title}
            </h2>

            {/* Client Info */}
            <div className="flex items-center gap-4 mb-6">
              <div className="p-2 rounded-lg bg-primary/10">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground">{caseStudy.client}</h3>
                <p className="text-muted-foreground">{caseStudy.industry}</p>
              </div>
            </div>

            {/* Challenge */}
            <div className="mb-8">
              <h4 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                The Challenge
              </h4>
              <p className="text-muted-foreground leading-relaxed">
                {caseStudy.challenge}
              </p>
            </div>

            {/* Solution */}
            <div className="mb-8">
              <h4 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                <Award className="h-5 w-5 text-primary" />
                Our Solution
              </h4>
              <p className="text-muted-foreground leading-relaxed">
                {caseStudy.solution}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button size="lg" className="group">
                Read Full Case Study
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button variant="outline" size="lg" className="group">
                View Results
                <ExternalLink className="ml-2 h-5 w-5 group-hover:scale-110 transition-transform" />
              </Button>
            </div>

            {/* Project Details */}
            <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-primary/5 border border-primary/10">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{caseStudy.duration}</div>
                <div className="text-sm text-muted-foreground">Duration</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">{caseStudy.teamSize}</div>
                <div className="text-sm text-muted-foreground">Team Size</div>
              </div>
            </div>
          </div>

          {/* Results Side */}
          <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
            <Card className="group hover:shadow-2xl transition-all duration-500 border-border/50 hover:border-primary/30 overflow-hidden">
              <CardContent className="p-0">
                {/* Header */}
                <div className="p-8 bg-gradient-to-r from-primary/5 to-primary/10 border-b border-border/50">
                  <h3 className="text-2xl font-bold text-foreground mb-2">Project Results</h3>
                  <p className="text-muted-foreground">Measurable impact and outcomes</p>
                </div>

                {/* Results */}
                <div className="p-8">
                  <div className="space-y-6">
                    {caseStudy.results.map((result, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <TrendingUp className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground leading-relaxed">{result}</span>
                      </div>
                    ))}
                  </div>

                  {/* Key Metrics */}
                  <div className="mt-8 pt-6 border-t border-border/50">
                    <h4 className="text-lg font-bold text-foreground mb-4">Key Metrics</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(caseStudy.metrics).map(([key, value], idx) => (
                        <div key={idx} className="text-center p-3 rounded-lg bg-muted/50">
                          <div className="text-xl font-bold text-primary">{value}</div>
                          <div className="text-xs text-muted-foreground capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Technologies */}
                  <div className="mt-8">
                    <h4 className="text-lg font-bold text-foreground mb-4">Technologies Used</h4>
                    <div className="flex flex-wrap gap-2">
                      {caseStudy.technologies.map((tech, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial */}
            <Card className="mt-8 group hover:shadow-lg transition-all duration-300 border-border/50 hover:border-primary/30">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-full bg-primary/10">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <blockquote className="text-muted-foreground italic mb-4">
                      "{caseStudy.testimonial.text}"
                    </blockquote>
                    <div className="text-sm">
                      <div className="font-semibold text-foreground">{caseStudy.testimonial.author}</div>
                      <div className="text-muted-foreground">{caseStudy.testimonial.position}</div>
                      <div className="text-primary font-medium">{caseStudy.testimonial.company}</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudyCard;
